import React from 'react';
import { useTranslation } from 'react-i18next';
import LoadingSpinner from '../core/LoadingSpinner';

interface GoogleSignInButtonProps {
  /**
   * Click handler for the button
   */
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  /**
   * Whether the button is in loading state
   */
  isLoading?: boolean;
  /**
   * Whether the button is disabled
   */
  disabled?: boolean;
  /**
   * Mode determines the button text: 'signin' or 'signup'
   */
  mode?: 'signin' | 'signup';
}

/**
 * GoogleSignInButton - Google Sign-In button with official logo
 *
 * Features:
 * - White background with light gray border (matching Microsoft button)
 * - Multi-color Google "G" logo
 * - Roboto font (with system fallback)
 * - Text color #1F1F1F
 *
 * @see https://developers.google.com/identity/branding-guidelines
 */
const GoogleSignInButton: React.FC<GoogleSignInButtonProps> = ({
  onClick,
  isLoading = false,
  disabled = false,
  mode = 'signin',
}) => {
  const { t } = useTranslation();

  const buttonText = mode === 'signin'
    ? t('common:auth.signInWithGoogle', 'Sign in with Google')
    : t('common:auth.signUpWithGoogle', 'Sign up with Google');

  const loadingText = t('common:status.connecting', 'Connecting...');

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={isLoading || disabled}
      className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      style={{ fontFamily: "'Roboto', system-ui, sans-serif" }}
    >
      {isLoading ? (
        <>
          <LoadingSpinner size="sm" color="gray" />
          <span className="text-sm font-medium text-[#1F1F1F]">
            {loadingText}
          </span>
        </>
      ) : (
        <>
          {/* Official Google "G" Logo - Multi-color */}
          <svg
            width="18"
            height="18"
            viewBox="0 0 18 18"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
              fill="#4285F4"
            />
            <path
              d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z"
              fill="#34A853"
            />
            <path
              d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
              fill="#FBBC05"
            />
            <path
              d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
              fill="#EA4335"
            />
          </svg>
          <span className="text-sm font-medium text-[#1F1F1F]">
            {buttonText}
          </span>
        </>
      )}
    </button>
  );
};

export default GoogleSignInButton;
